"use client"

import Link from "next/link"
import {
  IconAlertTriangle,
  IconCalendarCheck,
  IconCalendarPlus,
  IconCalendarX,
  IconPlaneArrival,
  IconPlaneDeparture,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAppointmentSheet } from "@/components/appointments/sheet/appointment-sheet-context"
import { TravelDeskFilters } from "@/components/travel-desk/travel-desk-filters"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

import type { TravelDeskLeg, TravelDeskRow } from "@/lib/travel-desk-api"
import type { UseTravelDeskResult } from "@/components/travel-desk/use-travel-desk"

/**
 * Travel Desk — the table half.
 *
 * The columns are the clinic's own list, in their order: partner, patient,
 * arrival, departure, driver, booked. Read-only on purpose — every cell that
 * represents something changeable links to the ONE surface that owns it (the
 * patient's trip, the dispatch board, the calendar), so the roster never
 * becomes a second place to edit a ride.
 */

/** `yyyy-MM-dd` → local midnight. `new Date("2026-07-30")` parses as UTC. */
function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

/** `HH:mm:ss` and `HH:mm` both arrive depending on who wrote the row. */
function shortTime(value: string | null): string | null {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})/.exec(value)
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : value
}

export function TravelDeskTable({
  board,
  embedded = false,
}: {
  board: UseTravelDeskResult
  /**
   * Rendered as a PANEL among others on a dashboard rather than as the whole
   * screen at /travel-desk.
   *
   * The difference is only chrome and who owns it: on the page the shell's
   * header carries the title and the filters, so the panel would be repeating
   * itself; embedded there is no page header to carry them, so the panel grows
   * its own eyebrow, filter row and summary line. The table, the scroller and
   * the pagination footer are identical either way — a trip must not read
   * differently depending on which surface opened it, which is the same rule
   * NeedsBookingTable's `gutter` prop exists to keep.
   */
  embedded?: boolean
}) {
  const t = useTranslations()
  const format = useFormatter()
  // Booking happens HERE. This board's whole reason for a `Booked` column is
  // that the coordinator has to do something about the No's, so the appointment
  // sheet opens with the patient — and their landing day — already filled in,
  // instead of sending them off to find the patient on another screen.
  const appointmentSheet = useAppointmentSheet()

  /**
   * The day to seed the booking form with.
   *
   * The arrival date, because a visit for this trip cannot sensibly fall before
   * the patient lands — the same "on or after the landing day" rule the `booked`
   * column is computed with. Undefined for a trip already under way, since
   * pre-filling a date in the past would be a worse guess than the sheet's own
   * default of now.
   */
  const bookingDate = (row: TravelDeskRow): Date | undefined => {
    if (!row.arrival_date) return undefined
    const arrival = parseLocalDate(row.arrival_date)
    if (!arrival) return undefined

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return arrival >= today ? arrival : undefined
  }

  const book = (row: TravelDeskRow) => {
    appointmentSheet.createAppointment(
      {
        leadId: row.lead_id,
        date: bookingDate(row),
        // The board counts clinic visits, so the appointment it creates has to
        // be one — otherwise booking from here would leave the row saying No.
        // Ignored on clinics without typed events, which is every clinic whose
        // appointments are all visits anyway.
        type: "clinic_visit",
      },
      // Flip the row's Booked cell without a manual refresh.
      { onChanged: board.reload },
    )
  }

  const formatDay = (value: string | null): string => {
    if (!value) return "—"
    const date = parseLocalDate(value)
    if (!date) return value
    return format.dateTime(date, { day: "numeric", month: "short" })
  }

  /**
   * One flight cell: the day above, the time and airport beneath it.
   *
   * A trip is frequently booked before the flight is, so a missing TIME is
   * normal and prints nothing rather than a placeholder that would read as
   * missing data. A missing DATE is different — that column is the reason the
   * row exists — so it keeps the em dash.
   */
  const FlightCell = ({
    date,
    time,
    airport,
  }: {
    date: string | null
    time: string | null
    airport: string | null
  }) => {
    const clock = shortTime(time)
    const meta = [clock, airport].filter(Boolean).join(" · ")

    return (
      <div className="min-w-0">
        <div className="text-[12.5px] font-medium">{formatDay(date)}</div>
        {meta !== "" && (
          <div className="truncate text-[11px] text-text-dim">{meta}</div>
        )}
      </div>
    )
  }

  /**
   * One driver cell.
   *
   * 🟥 Three states, not two, and the middle one is the whole reason this clinic
   * asked for the board: no ride at all (nothing to show), a ride nobody has
   * claimed yet (amber — someone must pick it up), and a claimed ride (the
   * driver's name). The clinic runs a pull model, so an unclaimed run can sit
   * there quietly until a coordinator notices it.
   */
  const DriverCell = ({ leg }: { leg: TravelDeskLeg | null }) => {
    if (leg === null) {
      return <span className="text-[11px] text-text-dim">—</span>
    }

    if (leg.driver === null) {
      return (
        <Badge
          variant="outline"
          className="border-amber-500/40 text-[10.5px] font-medium text-amber-600 dark:text-amber-500"
        >
          {t("travelDesk.table.unclaimed")}
        </Badge>
      )
    }

    return (
      <span
        className={cn(
          "text-[12px]",
          leg.status === "cancelled" && "text-text-dim line-through",
        )}
      >
        {leg.driver.name}
      </span>
    )
  }

  if (!board.isLoading && board.error) {
    return (
      <ApolloPanel className="p-8 text-center">
        <p className="text-[13px] text-text-dim">{t("travelDesk.error")}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={board.reload}>
          {t("travelDesk.retry")}
        </Button>
      </ApolloPanel>
    )
  }

  if (board.isLoading && board.rows.length === 0) {
    return (
      <ApolloPanel className="p-4">
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (board.rows.length === 0) {
    return (
      <ApolloPanel className="p-10 text-center">
        <IconPlaneArrival className="mx-auto mb-2 size-6 text-text-dim" />
        <p className="text-[13px] text-text-dim">{t("travelDesk.empty")}</p>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel
      // Only embedded: on the page these live in the shell's header.
      eyebrow={embedded ? t("travelDesk.title") : undefined}
      actions={embedded ? <TravelDeskFilters board={board} /> : undefined}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      // 🟥 Both halves are required. ApolloPanel's body is a plain `p-4` div —
      // `flex: 0 1 auto` with `min-height: auto` — so without this override it
      // sizes to its full content, the scroller inside inherits that height,
      // never overflows, and the panel simply CLIPS the rows at the fold with
      // no way to reach them. Overriding only the section (which does clip)
      // is what produced exactly that. Same pair needs-booking-table passes.
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
    >
      {embedded && (
        <div className="shrink-0 px-4 pb-1.5 pt-0.5">
          <TravelDeskSummary board={board} />
        </div>
      )}

      {/* The rows scroll, the pagination footer below does not — a coordinator
          paging through a month should not have to scroll to the bottom to find
          the Next button. `overflow-auto` covers both axes: the seven columns
          also need to scroll sideways on a narrow screen. */}
      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 text-[10.5px]">
                {t("travelDesk.table.affiliator")}
              </TableHead>
              <TableHead className="h-8 text-[10.5px]">
                {t("travelDesk.table.client")}
              </TableHead>
              <TableHead className="h-8 text-[10.5px]">
                <span className="inline-flex items-center gap-1">
                  <IconPlaneArrival className="size-3.5" />
                  {t("travelDesk.table.arrival")}
                </span>
              </TableHead>
              <TableHead className="h-8 text-[10.5px]">
                <span className="inline-flex items-center gap-1">
                  <IconPlaneDeparture className="size-3.5" />
                  {t("travelDesk.table.departure")}
                </span>
              </TableHead>
              <TableHead className="h-8 text-[10.5px]">
                {t("travelDesk.table.arrivalDriver")}
              </TableHead>
              <TableHead className="h-8 text-[10.5px]">
                {t("travelDesk.table.departureDriver")}
              </TableHead>
              <TableHead className="h-8 text-right text-[10.5px]">
                {t("travelDesk.table.booked")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {board.rows.map((row: TravelDeskRow) => (
              <TableRow
                key={row.travel_id}
                role="button"
                tabIndex={0}
                title={t("travelDesk.table.bookHint")}
                onClick={() => book(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    book(row)
                  }
                }}
                className="cursor-pointer"
              >
                <TableCell className="py-2">
                  {row.affiliator ? (
                    <span className="text-[12px]">{row.affiliator.name}</span>
                  ) : (
                    // An empty cell, not "Direct": only the partner portal
                    // writes this column, so the honest reading is "we have no
                    // partner recorded", which is not the same claim.
                    <span className="text-[11px] text-text-dim">—</span>
                  )}
                </TableCell>
                <TableCell className="py-2">
                  {/* The one escape from the row's booking click: the patient's
                      name still goes to their record. stopPropagation on both
                      handlers, or opening the file would also open the sheet
                      behind it. */}
                  <Link
                    href={`/leads/${row.lead_id}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    className="text-[12.5px] font-medium hover:underline"
                  >
                    {row.patient_name}
                  </Link>
                  {row.hotel_name && (
                    <div className="truncate text-[11px] text-text-dim">
                      {row.hotel_name}
                    </div>
                  )}
                </TableCell>
                <TableCell className="py-2">
                  <FlightCell
                    date={row.arrival_date}
                    time={row.arrival_time}
                    airport={row.arrival_airport}
                  />
                </TableCell>
                <TableCell className="py-2">
                  <FlightCell
                    date={row.departure_date}
                    time={row.departure_time}
                    airport={row.departure_airport}
                  />
                </TableCell>
                <TableCell className="py-2">
                  <DriverCell leg={row.arrival_transfer} />
                </TableCell>
                <TableCell className="py-2">
                  <DriverCell leg={row.departure_transfer} />
                </TableCell>
                <TableCell className="py-2 text-right">
                  {row.booked ? (
                    <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-600 dark:text-emerald-400">
                      <IconCalendarCheck className="size-3.5" />
                      {t("travelDesk.table.yes")}
                    </span>
                  ) : (
                    // The No doubles as the affordance: an unbooked row is the
                    // one the coordinator has to act on, so it carries the Book
                    // control rather than making them discover that the whole
                    // row is clickable. Same action either way.
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-amber-600 dark:text-amber-500">
                        <IconCalendarX className="size-3.5" />
                        {t("travelDesk.table.no")}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-6"
                        aria-label={t("travelDesk.table.book")}
                        title={t("travelDesk.table.book")}
                        onClick={(event) => {
                          // The row would fire too — harmless (same call) but it
                          // would open the sheet twice in dev StrictMode.
                          event.stopPropagation()
                          book(row)
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <IconCalendarPlus className="size-4" />
                      </Button>
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {board.meta && board.meta.last_page > 1 && (
        <div className="flex shrink-0 items-center justify-between border-t border-border px-4 py-2.5">
          <span className="text-[11.5px] text-text-dim">
            {t("travelDesk.pagination.page", {
              page: board.meta.current_page,
              total: board.meta.last_page,
            })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={board.page <= 1 || board.isLoading}
              onClick={() => board.setPage(board.page - 1)}
            >
              {t("travelDesk.pagination.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={board.page >= board.meta.last_page || board.isLoading}
              onClick={() => board.setPage(board.page + 1)}
            >
              {t("travelDesk.pagination.next")}
            </Button>
          </div>
        </div>
      )}
    </ApolloPanel>
  )
}

/**
 * The count strip that sits beside the filters — "12 trips · 4 need booking ·
 * 3 rides unclaimed". Lives here rather than in the filters file because it
 * reads the ROWS, and the filters own none of them.
 */
export function TravelDeskSummary({ board }: { board: UseTravelDeskResult }) {
  const t = useTranslations()

  if (board.meta === null) return null

  const needBooking = board.rows.filter((row) => !row.booked).length
  const unclaimed = board.rows.filter((row) => row.has_unclaimed_leg).length

  return (
    <div className="flex flex-wrap items-center gap-2.5 text-[11.5px] text-text-dim">
      <span>{t("travelDesk.summary.trips", { count: board.meta.total })}</span>
      {needBooking > 0 && (
        <span className="text-amber-600 dark:text-amber-500">
          {t("travelDesk.summary.needBooking", { count: needBooking })}
        </span>
      )}
      {unclaimed > 0 && (
        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-500">
          <IconAlertTriangle className="size-3.5" />
          {t("travelDesk.summary.unclaimed", { count: unclaimed })}
        </span>
      )}
    </div>
  )
}
